/**
 * The payload is scrubbed BEFORE it is sent — never after, and never
 * "probably". A mobile number sitting in a form field must not ride along in
 * a `message` or a `stack`, in either digit set, and a JWT-shaped string must
 * never leave the tab.
 *
 * No PII, no input values, no token.
 */

const REDACTED = '[redacted]';

const PATTERNS: readonly RegExp[] = [
  // Yemeni mobile numbers, ASCII: +9677XXXXXXXX · 009677XXXXXXXX · 9677XXXXXXXX.
  // The bare 7XXXXXXXX is caught by the trunk-form pattern two lines down.
  /(?:\+|00)?967[\s-]?7\d{8}/g,
  // The RETIRED Iraqi market, ASCII: +9647XXXXXXXXX · 009647XXXXXXXXX · 07XXXXXXXXX.
  // No new sign-in can be written under it and the stored rows are still here,
  // so a number in this shape still must not ride along in a `message`.
  /(?:\+|00)?964[\s-]?7\d{8,9}/g,
  /\b0?7[\s-]?\d{8,9}\b/g,
  // The same shapes written in Arabic-Indic digits.
  //
  // **No word boundary here.** JavaScript's word-boundary escape is ASCII-only and an
  // Arabic-Indic digit is not a word character, so the trunk-form pattern
  // demanded a word character in front of the number and never matched one
  // written after a space. It silently redacted nothing — the exact failure
  // this list exists to prevent, for the keyboard most of these readers use.
  /(?:\+|٠٠)?٩٦٧[\s-]?٧[٠-٩]{8}/g,
  /(?:\+|٠٠)?٩٦٤[\s-]?٧[٠-٩]{8,9}/g,
  /٠?٧[\s-]?[٠-٩]{8,9}/g,
  // JWT-shaped strings — header.payload.signature in base64url.
  /\beyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\b/g,
  // Bearer credentials of any shape.
  /Bearer\s+[A-Za-z0-9._-]+/gi,
  // E-mail addresses.
  /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g,
];

export function scrub(text: string): string {
  return PATTERNS.reduce((value, pattern) => value.replace(pattern, REDACTED), text);
}
